import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { OrderingSelectors } from 'polygon-ordering';
import getThemeLookup from '../selectors/getThemeLookup';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import getDisplayableSaleTypes from '../selectors/getDisplayableSaleTypes';
import getOrderInProgress from '../selectors/getOrderInProgress';
import combineStyles from '../utils/combineStyles';
import { COMPLETED_ORDER_SCREEN_ROUTE, MENU_SCREEN_ROUTE } from '../hooks/useRoutes';
import ScreenHero from '../components/ScreenHero';
import ScreenFloating from '../components/ScreenFloating';
import StandardButton from '../components/StandardButton';
import Text from '../components/Text';
import RedirectAndPreserveSearch from '../components/RedirectAndPreserveSearch';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { Row, Col } from 'reactstrap';
import { RiHeart3Line, RiTimeLine } from 'react-icons/ri';
import { PAST_ORDERS_SCREEN_ROUTE } from './PastOrdersScreen';
import { FAVOURITES_SCREEN_ROUTE } from './FavouritesScreen';
import history from '../history';
import getDisabledLoyaltyRoutes from '../utils/getDisabledLoyaltyRoutes';
import getConfig from '../selectors/getConfig';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { SIGN_IN_MODAL_ID } from '../modals/SignInModal';
import SaleTypeListItem from '../components/SaleTypeListItem';
import { OrderingOperations } from 'polygon-ordering';

const { getOrderSubmitted, getMember, getBypassDeliveryDetails } = OrderingSelectors;
const { setBypassDeliveryDetails } = OrderingOperations;

const InitialScreen = () => {
  const p = useAppSelector(getThemeLookup);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const displayableSaleTypes = useAppSelector(getDisplayableSaleTypes);
  const orderInProgress = useAppSelector(getOrderInProgress);
  const orderSubmitted = useAppSelector(getOrderSubmitted);
  const member = useAppSelector(getMember);
  const dispatch = useAppDispatch();
  const config = useAppSelector(getConfig);
  const disabledLoyaltyRoutes = getDisabledLoyaltyRoutes(config);
  const loyaltyDisabled = useAppSelector(state => state.config.disableLoyalty);
  const currentModalId = useAppSelector(state => state.currentModalId);

  const { t } = useTranslation();
  const pathname = useLocation();
  const bypassDeliveryDetails = useAppSelector(getBypassDeliveryDetails);

  useEffect(() => {
    // if loyalty is disabled, or if the member is logged in, don't show the sign in modal
    // Also, in cases where there is already a modal showing, don't replace it with the sign in modal.
    if (!loyaltyDisabled && !member && !currentModalId) {
      dispatch(setCurrentModal(SIGN_IN_MODAL_ID));
    }
  }, []);

  const getLocationId = () => {
    let locationId = null;

    if (pathname.state) {
      locationId = pathname.state.locationId;
    }

    return locationId;
  };

  const ScreenComponent = deviceTypeMobile ? ScreenHero : ScreenFloating;

  const columnStyles = combineStyles(styles.column, deviceTypeMobile && styles.columnMobile);

  if (orderSubmitted) {
    return <RedirectAndPreserveSearch to={COMPLETED_ORDER_SCREEN_ROUTE} />;
  }

  if (orderInProgress) {
    return <RedirectAndPreserveSearch to={MENU_SCREEN_ROUTE} />;
  } else {
    if (bypassDeliveryDetails) {
      dispatch(setBypassDeliveryDetails(false));
    }
  }

  const showFavourites = !disabledLoyaltyRoutes.includes(FAVOURITES_SCREEN_ROUTE);
  const showPastOrders = !disabledLoyaltyRoutes.includes(PAST_ORDERS_SCREEN_ROUTE);

  return (
    <ScreenComponent>
      <div
        style={combineStyles(
          deviceTypeMobile ? styles.mainContainerMobile : styles.mainContainerDesktop,
          p('screen', ['backgroundColor']),
          p(deviceTypeMobile ? 'initialScreenMobile' : 'initialScreenDesktop', [
            'backgroundColor',
            'boxShadow',
          ]),
        )}
      >
        <div style={columnStyles}>
          {!deviceTypeMobile && (
            <div className="text-center mb-3">
              <Text variant="h1" value={t('title.newOrder')} />
            </div>
          )}

          {(displayableSaleTypes || [])?.map(saleType => (
            <SaleTypeListItem saleType={saleType} locationId={getLocationId()} key={saleType} />
          ))}

          {member && !loyaltyDisabled && (
            <Row
              style={{
                marginTop: 'auto',
              }}
            >
              {showFavourites && (
                <Col className=" m-0 p-0">
                  <StandardButton
                    onClick={() => history.push(FAVOURITES_SCREEN_ROUTE)}
                    label="Favourites"
                    themeKey="pickSaleTypeBottom"
                    LeftIconComponent={RiHeart3Line}
                    leftIconStyle={combineStyles(
                      {
                        fontSize: 35,
                      },
                      p('saleTypeIcons', ['color']),
                      p('pickSaleTypeBottomIcons', ['color']),
                    )}
                    containerStyle={{
                      gap: 4,
                    }}
                  />
                </Col>
              )}
              {showPastOrders && (
                <Col className=" m-0 p-0">
                  <StandardButton
                    onClick={() => history.push(PAST_ORDERS_SCREEN_ROUTE)}
                    label="Past Orders"
                    themeKey="pickSaleTypeBottom"
                    LeftIconComponent={RiTimeLine}
                    leftIconStyle={combineStyles(
                      {
                        fontSize: 35,
                      },
                      p('saleTypeIcons', ['color']),
                      p('pickSaleTypeBottomIcons', ['color']),
                    )}
                    containerStyle={{
                      gap: 4,
                    }}
                  />
                </Col>
              )}
            </Row>
          )}
        </div>
      </div>
    </ScreenComponent>
  );
};

const styles = {
  mainContainerDesktop: {
    borderRadius: 5,
    padding: 30,
    paddingTop: 50,
    paddingBottom: 50,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    margin: 'auto', // https://stackoverflow.com/a/33455342/8706204
    maxWidth: 550,
    width: '100%',
    justifyContent: 'center',
    marginTop: '5vh',
    minHeight: 'calc(100% - 5vh * 2)',
  },
  mainContainerMobile: {
    flex: 1,
    padding: '10px 25px 100px 25px',
  },
  column: {
    maxWidth: 340,
    minHeight: 500,
    gap: 15,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  columnMobile: {
    maxWidth: 'auto',
    padding: '20px 0 0 0',
    height: '100%',
  },
  button: {
    margin: '20px 20px',
  },
  verticalSeperator: {
    width: 1.5,
    marginLeft: 40,
    marginRight: 40,
  },
};

export default InitialScreen;
